import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseDetail,
  ReleasePrerequisite,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  ProgressBar,
  StatusBadge,
} from "../../shared/ui";
import { CopyPlanFromPrevious } from "./CopyPlanFromPrevious";
import { PrerequisiteDrawer } from "./PrerequisiteDrawer";
import { PREREQUISITE_STATUS_LABELS, PREREQUISITE_STATUS_TONE } from "./releaseVocabulary";

/**
 * §26's checklist.
 *
 * <p>Rows rather than a textarea, which is the whole of §26's point: a paragraph saying "backups
 * taken, access confirmed" cannot be assigned to anybody, chased when it is late, failed, or made
 * to stop a release. Each of those is something a row does.
 *
 * <p>Mandatory items are marked, because those are the ones that fail the readiness gate. An
 * optional prerequisite left undone is a note; a mandatory one is a reason the release does not go.
 */
export function ReleasePrerequisitesTab({
  release,
  leg,
  onChanged,
}: {
  release: ReleaseDetail;
  /**
   * The one leg of the journey being worked on, or null for the release as a whole (§63).
   *
   * <p>When set, the list narrows to what applies here: items tagged for this environment plus
   * the ones tagged for none, which are true of every deployment. Somebody standing in a
   * production window should not have to read past a sandbox's database reset to find their own
   * next step.
   */
  leg?: EnvironmentView | null;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<ReleasePrerequisite | null>(null);
  const [adding, setAdding] = useState(false);

  const prerequisites = useQuery({
    queryKey: ["release-prerequisites", release.id],
    queryFn: () =>
      api.get<ReleasePrerequisite[]>(`/api/v1/releases/${release.id}/prerequisites`),
  });

  const rows = (prerequisites.data ?? []).filter(
    (row) => !leg || row.environment == null || row.environment.id === leg.id,
  );
  const mandatory = rows.filter((row) => row.mandatory);
  const settled = mandatory.filter(
    (row) => row.status === "COMPLETED" || row.status === "SKIPPED",
  );

  return (
    <Card>
      <CardHeader
        title={leg ? `Pre-requisites — ${leg.name}` : "Pre-requisites"}
        subtitle={
          mandatory.length === 0
            ? "Nothing mandatory yet."
            : `${settled.length} of ${mandatory.length} mandatory items settled`
        }
        action={
          can("RELEASE_EDIT") && (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              Add
            </Button>
          )
        }
      />
      <CardBody className="space-y-3">
        {mandatory.length > 0 && (
          <ProgressBar
            value={settled.length}
            total={mandatory.length}
            tone={settled.length === mandatory.length ? "green" : "amber"}
            label="Mandatory prerequisites"
          />
        )}

        {prerequisites.isPending ? (
          <LoadingState label="Loading prerequisites…" />
        ) : rows.length === 0 ? (
          <>
            <EmptyState
              title="No prerequisites yet"
              message="The things that must be true before this release can go — access, backups, artifacts, notifications."
              action={
                can("RELEASE_EDIT") ? (
                  <Button onClick={() => setAdding(true)}>Add the first one</Button>
                ) : undefined
              }
            />
            {/* Offered where it is noticed. Standing in an empty Production runbook and being
                told the fix is one screen back is how people end up retyping it. */}
            {leg && (
              <CopyPlanFromPrevious release={release} leg={leg} onCopied={onChanged} />
            )}
          </>
        ) : (
          groupByEnvironment(rows).map(([label, group]) => (
          <div key={label}>
            {/*
              §63: separate portions per environment, so a production snapshot and a sandbox
              reset are not one undifferentiated list. Only shown once something is actually
              tagged — a heading over every prerequisite when they all apply everywhere is a
              label that carries no information.
            */}
            {groupByEnvironment(rows).length > 1 && (
              <p className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-ink-400 first:mt-0">
                {label}
              </p>
            )}
          <ol className="divide-y divide-ink-100">
            {group.map((row, position) => (
              <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="flex min-w-0 items-start gap-2.5">
                  {/*
                    Numbered within the list being read, not by the release-wide sequence.
                    Production's first prerequisite showing as "2" reads as Development's second
                    one — the same record seen from another leg, which it is not.
                  */}
                  <span className="tnum mt-0.5 w-5 shrink-0 text-xs text-ink-400">
                    {position + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {row.title}
                      {!row.mandatory && (
                        <span className="ml-2 text-xs font-normal text-ink-500">optional</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {row.category.replace(/_/g, " ").toLowerCase()}
                      {row.owner && ` · ${row.owner.fullName}`}
                      {row.completedBy && ` · completed by ${row.completedBy.fullName}`}
                    </p>
                    {row.completionComment && (
                      <p className="mt-1 rounded-md bg-ink-50 px-2 py-1 text-xs text-ink-700">
                        {row.completionComment}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge tone={PREREQUISITE_STATUS_TONE[row.status]}>
                    {PREREQUISITE_STATUS_LABELS[row.status]}
                  </StatusBadge>
                  {can("RELEASE_EDIT") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Check className="h-4 w-4" />}
                      aria-label={`Update ${row.title}`}
                      onClick={() => setEditing(row)}
                    >
                      Update
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ol>
          </div>
          ))
        )}
      </CardBody>

      <PrerequisiteDrawer
        release={release}
        prerequisite={editing}
        leg={leg}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={onChanged}
      />
    </Card>
  );
}

/**
 * Prerequisites in per-environment groups, with the ones that apply everywhere first.
 *
 * <p>Ordered that way deliberately: what is true of every deployment is the common case and the
 * thing somebody reads first, and burying it under a "Production" heading would make the general
 * case look like a special one.
 */
function groupByEnvironment(
  rows: ReleasePrerequisite[],
): [string, ReleasePrerequisite[]][] {
  const groups = new Map<string, ReleasePrerequisite[]>();
  for (const row of rows) {
    const label = row.environment?.name ?? "Every environment";
    groups.set(label, [...(groups.get(label) ?? []), row]);
  }
  return [...groups.entries()].sort(([a], [b]) =>
    a === "Every environment" ? -1 : b === "Every environment" ? 1 : a.localeCompare(b),
  );
}
